import * as React from 'react';

import { DataGridColumn } from '@rematter/ui-web';

import { useTranslations } from '../../i18n/useTranslations';
import { InventoryLotRecord } from '../../types/inventoryLot';
import { formatQuantity } from '../../utils/unitConversion';

import { LotStatusBadge } from './LotStatusBadge';

type UseInventoryLotsColumnsArgs = {
  onRowClick?: (cell: { row: InventoryLotRecord }) => void;
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return '—';
  const date = new Date(dateStr);
  if (isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const renderLotNumberCell = ({ row }: { row: InventoryLotRecord }) => (
  <div className="flex flex-col">
    <span className="font-medium">{row.lotNumber}</span>
    {row.description && (
      <span className="text-xs text-neutral/60">{row.description}</span>
    )}
  </div>
);

const renderFacilityCell = ({ row }: { row: InventoryLotRecord }) => (
  <span className="text-sm">{row.facilityName || '—'}</span>
);

const renderStatusCell = ({ row }: { row: InventoryLotRecord }) => (
  <LotStatusBadge status={row.status} />
);

const renderItemsCell = ({ row }: { row: InventoryLotRecord }) => (
  <span className="text-sm tabular-nums">{row.totalItemCount}</span>
);

const renderCreatedAtCell = ({ row }: { row: InventoryLotRecord }) => (
  <span className="text-sm">{formatDate(row.createdAt)}</span>
);

export const useInventoryLotsColumns = ({
  onRowClick,
}: UseInventoryLotsColumnsArgs = {}): DataGridColumn<InventoryLotRecord>[] => {
  const t = useTranslations();

  const renderQuantityCell = React.useCallback(
    ({ row }: { row: InventoryLotRecord }) => {
      const { quantitySummary } = row;

      if (!quantitySummary || quantitySummary.length === 0) {
        return (
          <span className="text-sm text-neutral/60">
            {t.lotsListView.table.emptyLot}
          </span>
        );
      }

      const primaryItem = quantitySummary[0];
      const additionalCount = quantitySummary.length - 1;

      return (
        <div className="flex flex-col">
          <span className="text-sm tabular-nums">
            {formatQuantity(primaryItem.quantity, primaryItem.quantityUnit)}
          </span>
          <span className="max-w-[150px] truncate text-xs text-neutral/60">
            {primaryItem.itemName}
          </span>
          {additionalCount > 0 && (
            <span className="text-xs text-neutral/60">
              {t.lotsListView.table.moreItems(additionalCount)}
            </span>
          )}
        </div>
      );
    },
    [t],
  );

  return React.useMemo(() => {
    const columns: DataGridColumn<InventoryLotRecord>[] = [
      {
        key: 'lotNumber',
        header: t.lotsListView.table.columns.lotNumber,
        onClick: onRowClick,
        cell: renderLotNumberCell,
        width: 250,
        sortable: true,
      },
      {
        key: 'facility',
        header: t.lotsListView.table.columns.facility,
        onClick: onRowClick,
        cell: renderFacilityCell,
        width: 200,
      },
      {
        key: 'status',
        header: t.lotsListView.table.columns.status,
        onClick: onRowClick,
        cell: renderStatusCell,
        width: 120,
      },
      {
        key: 'quantity',
        header: t.lotsListView.table.columns.quantity,
        onClick: onRowClick,
        cell: renderQuantityCell,
        width: 180,
      },
      {
        key: 'items',
        header: t.lotsListView.table.columns.items,
        onClick: onRowClick,
        cell: renderItemsCell,
        width: 100,
      },
      {
        key: 'createdAt',
        header: t.lotsListView.table.columns.createdAt,
        onClick: onRowClick,
        cell: renderCreatedAtCell,
        width: 150,
        sortable: true,
      },
    ];

    return columns;
  }, [t, onRowClick, renderQuantityCell]);
};
